import { useCallback } from "react";
import styled from "styled-components";
import { ChatBubbleBottomCenterIcon } from "@heroicons/react/24/outline";
import { useT } from "@sablier/locales";
import { Input, Label, Spinner } from "@sablier/v2-components";
import { tags } from "@sablier/v2-constants";
import { _ } from "@sablier/v2-mixins";
import { IToken } from "@sablier/v2-types/token";
import vendors from "@sablier/v2-utils/vendors";
import { Characteristic, Warning } from "~/client/components/molecules";
import { useModalTokenPicker } from "~/client/hooks";
import { useAutofillPrepare, useAutofillWarning } from "../logic";
import Footer from "./Footer";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    width: 100%;
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    padding-right: calc(${(props) => props.theme.sizes.edge} * 2);
    padding-left: calc(${(props) => props.theme.sizes.edge} * 2);
  }
`;

const Query = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1);
    border: 2px solid ${(props) => props.theme.colors.border};
    border-radius: 8px;
    background-color: ${(props) => props.theme.colors.dark050};
  }
`;

const Loader = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    justify-content: center;
    width: 100%;
    height: 200px;
  }
`;

const Description = styled.div`
  & > p {
    ${(props) => props.theme.styles.textParagraph}
    & {
      color: ${(props) => props.theme.colors.gray200};
      line-height: 20pt;
    }
  }
`;

const Bundler = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;

    & > *:first-child {
      border: 2px solid ${(props) => props.theme.colors.yellow50};
      background-color: ${(props) => props.theme.colors.yellow10};
    }

    & > *:nth-child(2) {
      *[data-component="box"] {
        --background: ${(props) => props.theme.colors.dark050};
      }
    }
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${Content} {
    ${(props) => props.theme.medias.maxMD} {
      padding-right: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      padding-left: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    }
    ${(props) => props.theme.medias.maxSM} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      padding-right: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      padding-left: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    }
  }
`;

function Bundle() {
  const { t } = useT();
  const warning = useAutofillWarning();
  const { setOpen } = useModalTokenPicker();

  const onToken = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "tokenPicker",
        placeKey: "createSingle",
      });
    });

    setOpen(true, {
      onChoose: (value?: IToken) => {
        vendors.track.log((events) => {
          return events.selectToken(value?.name ?? "");
        });
      },
    });
  }, [setOpen]);

  if (warning.state === "connect") {
    return <Warning.Connect />;
  }

  if (warning.state === "chain") {
    return <Warning.Chain toChainId={warning.chainId} />;
  }

  if (warning.state === "token") {
    return (
      <>
        <Warning.Token chainId={warning.chainId} address={warning.address} />
        <Input.TokenPicker
          id={"unknown-token"}
          isBordered
          onChange={onToken}
          placeholder={t("modalCards.autofill.create.token")}
        />
      </>
    );
  }

  if (warning.state === "numeric") {
    return <Warning.Numeric value={warning.value} purpose={warning.purpose} />;
  }
  return false;
}

function Body() {
  const { t } = useT();
  const { isLoading, warning } = useAutofillPrepare();

  return (
    <Wrapper data-cy={tags.modalAutofillCreate}>
      <Content>
        <Description>
          <p>{t("modalCards.autofill.create.description")}</p>
        </Description>
        {isLoading ? (
          <Loader>
            <Spinner color={"white"} thickness={3} size={120} />
          </Loader>
        ) : (
          <>
            {warning.state === "ready" ? (
              <Query>
                <Label
                  icon={ChatBubbleBottomCenterIcon}
                  value={t("modalCards.autofill.create.label")}
                />
                <Characteristic.Autofill {...warning.value} />
              </Query>
            ) : (
              <Bundler>
                <Bundle />
              </Bundler>
            )}
          </>
        )}
      </Content>
      <Footer />
    </Wrapper>
  );
}

export default Body;
